import { memo } from 'react';
import type { EnrichedOpportunity, TradeSignal } from '@/models/marketData';
import { getSignalLabel } from '@/utils/marketCalculations';

const colors = ['#24dcb0', '#27b7f5', '#ffc568'];
export const TechnicalAnalytics = memo(function TechnicalAnalytics({
  opportunities,
}: {
  opportunities: EnrichedOpportunity[];
}) {
  const count = opportunities.length;
  const signals = ([5, 4, 3] as TradeSignal[]).map((signal) => ({
    name: getSignalLabel(signal),
    value: opportunities.filter((item) => item.signal === signal).length,
  }));
  const first = (signals[0].value / (count || 1)) * 100;
  const second = first + (signals[1].value / (count || 1)) * 100;
  const strengths = [
    { name: 'EMA aligned', value: opportunities.filter((item) => item.metrics.emaAligned).length },
    {
      name: 'SuperTrend positive',
      value: opportunities.filter((item) => item.indicators.superTrendBullish).length,
    },
    { name: 'ADX ≥ 25', value: opportunities.filter((item) => item.indicators.adx14 >= 25).length },
    {
      name: 'MACD bullish',
      value: opportunities.filter((item) => item.metrics.macdBullish).length,
    },
    {
      name: 'Volume ≥ 1.5×',
      value: opportunities.filter((item) => item.indicators.volumeRatio20 >= 1.5).length,
    },
  ];
  return (
    <section className="panel analytics-panel">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Breadth monitor</span>
          <h2>Technical strength</h2>
        </div>
        <span>Across {count} signals</span>
      </div>
      <div className="breadth-layout">
        <div
          className="breadth-donut"
          role="img"
          aria-label={signals.map((item) => `${item.name}: ${item.value}`).join(', ')}
          style={{
            background: count
              ? `conic-gradient(${colors[0]} 0 ${first}%, ${colors[1]} ${first}% ${second}%, ${colors[2]} ${second}% 100%)`
              : 'var(--line)',
          }}
        >
          <div>
            <strong>{count}</strong>
            <span>Signals</span>
          </div>
        </div>
        <div className="signal-legend">
          {signals.map((item, index) => (
            <div key={item.name}>
              <i style={{ background: colors[index] }} />
              <span>{item.name}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
        </div>
        <div className="breadth-bars">
          {strengths.map((item) => (
            <div className="breadth-row" key={item.name}>
              <span>{item.name}</span>
              <meter min={0} max={count || 1} value={item.value} aria-label={item.name} />
              <strong>{item.value}</strong>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});
